import { Trans } from '@lingui/react/macro';
import React from 'react';
import { Pages } from 'modules/navigation/domain/enums/pages';
import {
  devicesRecorderButton,
  devicesRecorderModalVmix,
  devicesRecorderSavedVmix,
  vmixAddInput,
  vmixAddShortcut,
  vmixApi,
  vmixInputColour,
  vmixInputBrowser,
  vmixInputGear,
  vmixInputRename,
  vmixInputRenamed,
  vmixMarkersRecording,
  vmixPanelConnected,
  vmixRecordButton,
  vmixRecordGear,
  vmixRecordingConfirmation,
  vmixRecordingSetup,
  vmixRestart,
  vmixSaveButton,
  vmixSaveDialog,
  vmixSettingsButton,
  vmixShortcutF9,
  vmixShortcuts,
  vmixWebController,
} from 'modules/video-markers/ui/assets/guide';
import UDLink from 'ud-ui/components/link';
import UDText from 'ud-ui/components/text';
import GuideStep from './guide-step';

/** Настройка vMix по шагам. Подписи — как в русском интерфейсе vMix 28, в скобках — английские. */
function VmixGuide() {
  return (
    <div className="flex flex-col gap-4">
      <GuideStep
        number={1}
        title={<Trans>Откройте табло доступ к Web API</Trans>}
        clickShots={[vmixWebController]}
        resultShot={vmixApi}
      >
        <UDText variant="body-2">
          <Trans>
            Вверху справа <b>«Настройки»</b> (Settings) → слева <b>«Веб-контроллера»</b> (Web Controller). Галочка «Включено» и порт{' '}
            <b>8088</b> — как на скриншоте. Снимите галочку <b>«Включить повышенную безопасность в Интернете и TCP API»</b> (Enable
            enhanced security) и нажмите <b>OK</b>.
          </Trans>
        </UDText>
        <UDText variant="body-2">
          <Trans>
            Без этого vMix отвечает табло «Browser script access not permitted» и видеометки не подключаются. Делайте это до шага 6:
            веб-инпут, добавленный раньше, разрешение не получит — его придётся закрыть и добавить заново. Цена: пока галочка снята,
            страницы, открытые в браузере этого компьютера, могут обращаться к vMix; доступ из интернета закрывает соседняя галочка
            «Ограничить доступ только к LAN» — её не снимайте.
          </Trans>
        </UDText>
        <UDText variant="body-2">
          <Trans>
            Проверка: откройте в браузере на этом компьютере <b>http://127.0.0.1:8088/api</b> — должен открыться текст с тегами (XML).
          </Trans>
        </UDText>
      </GuideStep>

      <GuideStep
        number={2}
        title={<Trans>Настройте запись</Trans>}
        clickShots={[vmixRecordGear, vmixRecordingSetup]}
        resultShot={null}
      >
        <UDText variant="body-2">
          <Trans>
            Внизу окна vMix нажмите шестерёнку слева от кнопки <b>«Запись»</b> (Record) — откроется окно <b>Recording Setup</b>. Формат
            можно оставить <b>MP4</b>, файл ляжет в «Документы\vMixStorage» (поле «Имя файла»). Нажмите <b>OK</b>. Без этого первая
            запись не начнётся, а откроет это окно.
          </Trans>
        </UDText>
      </GuideStep>

      <GuideStep
        number={3}
        title={<Trans>Добавьте служебный инпут для клавиши метки</Trans>}
        clickShots={[vmixAddInput, vmixInputColour, vmixInputGear, vmixInputRename, vmixSaveButton, vmixSaveDialog]}
        resultShot={vmixInputRenamed}
      >
        <UDText variant="body-2">
          <Trans>
            Слева внизу нажмите <b>«Добавить входного…»</b> (Add Input) → слева <b>«Цвет»</b> (Colour) → <b>OK</b>. В списке инпутов
            появится инпут «Colour».
          </Trans>
        </UDText>
        <UDText variant="body-2">
          <Trans>
            Нажмите шестерёнку в правом нижнем углу его плитки → вкладка <b>«Общий»</b> → в поле <b>«Название»</b> введите ровно{' '}
            <b>ImGame метка</b> и закройте окно. Правый клик по заголовку плитки инпут не переименовывает, а сворачивает — повторный
            правый клик разворачивает обратно. В эфир этот инпут не выводите.
          </Trans>
        </UDText>
        <UDText variant="body-2">
          <Trans>
            Сохраните пресет: вверху кнопка <b>«Сохранить»</b> (Save) → имя файла, например «ImGame трансляция» → <b>«Сохранить»</b>.
            Дальше открывайте матчи из него — клавиша метки привязана именно к этому инпуту.
          </Trans>
        </UDText>
      </GuideStep>

      <GuideStep
        number={4}
        title={<Trans>Назначьте клавишу метки</Trans>}
        clickShots={[vmixSettingsButton, vmixShortcuts, vmixAddShortcut, vmixRestart]}
        resultShot={vmixShortcutF9}
      >
        <UDText variant="body-2">
          <Trans>
            Вверху справа <b>«Настройки»</b> (Settings) → слева <b>«Ярлыки»</b> (Shortcuts) → <b>«Добавлять»</b> (Add). В окне Add
            Shortcut: <b>«Ключ / Управление»</b> — клавиша, например <b>F9</b>; <b>«Функции»</b> — <b>LoopOn</b> (именно LoopOn, а не
            Loop); <b>«Вход»</b> — <b>ImGame метка</b>. «Вход» по умолчанию стоит на Preview — его обязательно поменять. Нажмите{' '}
            <b>OK</b>, затем <b>OK</b> в настройках.
          </Trans>
        </UDText>
        <UDText variant="body-2">
          <Trans>
            vMix спросит «Restart vMix to apply changes?» — нажмите <b>«Да»</b>. Это нормально: vMix перезапустится и откроется с теми
            же инпутами (из автосохранения last.vmix), ничего заново настраивать не нужно.
          </Trans>
        </UDText>
        <UDText variant="body-2">
          <Trans>
            Клавиша vMix срабатывает, только когда окно vMix активно. Зачем эта клавиша и что будет при разных нажатиях — в блоке
            «Клавиша метки» в начале страницы.
          </Trans>
        </UDText>
      </GuideStep>

      <GuideStep
        number={5}
        title={<Trans>Скопируйте ссылку на табло</Trans>}
        clickShots={[devicesRecorderButton, devicesRecorderModalVmix]}
        resultShot={devicesRecorderSavedVmix}
      >
        <UDText variant="body-2">
          <Trans>
            В админке откройте <UDLink to={Pages.DEVICES.INDEX}>«Устройства»</UDLink> и нажмите кнопку <b>«Видеометки: …»</b>. В поле
            «Программа» выберите <b>vMix</b> — порт сам станет <b>8088</b>, пароль vMix не нужен. Нажмите «Сохранить»: кнопка станет
            называться «Видеометки: vMix». В строке устройства нажмите иконку-цепочку <b>«Копировать ссылку на табло»</b>.
          </Trans>
        </UDText>
      </GuideStep>

      <GuideStep
        number={6}
        title={<Trans>Добавьте табло в vMix и проверьте подключение</Trans>}
        clickShots={[vmixAddInput, vmixInputBrowser]}
        resultShot={vmixPanelConnected}
      >
        <UDText variant="body-2">
          <Trans>
            <b>«Добавить входного…»</b> (Add Input) → слева <b>«Веб-браузер»</b> (Web Browser) → в поле <b>URL</b> вставьте ссылку из
            шага 5, <b>«Ширина»</b> — 1920, <b>«Высота»</b> — 1080 (vMix подставляет размер вашего проекта — поменяйте) → <b>OK</b>.
          </Trans>
        </UDText>
        <UDText variant="body-2">
          <Trans>
            Нажмите по картинке нового инпута — табло откроется в левом окне «Предуста» (Preview) вместе с панелью управления. В блоке
            «Видеометки · vMix» должно быть <b>«vMix подключён»</b> зелёным. «Нет подключения к vMix» — вернитесь к шагу 1, снимите
            галочку повышенной безопасности, затем закройте этот инпут (<b>Close</b> на плитке) и добавьте его заново.
          </Trans>
        </UDText>
      </GuideStep>

      <GuideStep
        number={7}
        title={<Trans>Проверьте метки</Trans>}
        clickShots={[vmixRecordButton, vmixRecordingConfirmation]}
        resultShot={vmixMarkersRecording}
      >
        <UDText variant="body-2">
          <Trans>
            Внизу нажмите <b>«Запись»</b> (Record). При первой записи vMix покажет окно «Recording Confirmation» с настройками записи —
            нажмите <b>Record</b>; разрешение в нём — формат вашего проекта vMix, на метки оно не влияет.
          </Trans>
        </UDText>
        <UDText variant="body-2">
          <Trans>
            В панели (шаг 6) появится «● Запись» с таймером. Отметьте событие матча в приложении статиста — счётчик «События»
            увеличится. С окном vMix в фокусе нажмите клавишу метки — увеличится счётчик «ручные». Время у vMix — с точностью до
            секунды.
          </Trans>
        </UDText>
      </GuideStep>
    </div>
  );
}

export default React.memo(VmixGuide);
